import React, { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, InputAdornment, Stack, Typography } from "@mui/material";

import { adminSettingLDAPSettingsSchemas } from "../../../schemas/adminSettingPage";
import { RFHTextField as RHFTextField } from '../../../components/RFH'
import useMutate from "../../../hooks/useMutate";
import { updateTenantLDAP } from "../../../sevices/apiSystemSettings";
import { useResponsive } from "../../../hooks/useResponsive";
import { parseLdapUrl } from "../../../utils";
import { CustomButton } from "../../../components/Button/Button";

function LDAPSettingsBindCred({ initialData }) {
  const { mutate, isPending } = useMutate(updateTenantLDAP, ["tenant-details"]);
  const methods = useForm({
    resolver: yupResolver(adminSettingLDAPSettingsSchemas),
    defaultValues: initialData,
  });
  const isMobile = useResponsive("down", "md");

  // Watch form values for real-time changes (optional)
  const formValues = methods.watch();

 // Function to check if any field has a value
 const isAnyFieldFilled = useCallback(() => {
  return Object.values(formValues).some((value, index) => {
    const initialValue = Object.values(initialData)[index];
    return value !== undefined && value !== "" && value !== null && value !== initialValue;
  });
}, [formValues, initialData]);

  const onSubmit = useCallback((formData) => {
    const { host = "", port = "" } = formData.ldap_url ? parseLdapUrl(formData.ldap_url) : {};
    const data = {
      ldap_host: host ? host : null,
      ldap_port: port ? port : null,
      ldap_base_dn: formData.ldap_base_dn ? formData.ldap_base_dn : null,
      ldap_user: formData.ldap_user ? formData.ldap_user : null,
      ldap_password: formData.ldap_password ? formData.ldap_password : null
    }
    mutate(data);
  }, [mutate]);

  return (
    <>
      <FormProvider {...methods}>
        <div className="mt-32 form-section  ">
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div>
              <Typography className="setting-heading mb-2  ml-0">
                LDAP Setting
              </Typography>
              <div className="mb-4 ldap-note">
                <Typography className="logo-note mb-2">
                  If your team uses an LDAP server, you can enter the LDAP
                  server settings below for user authentication. Please note:
                </Typography>
                <ul>
                  <li className="logo-note">
                    All LDAP users must still be added to the system to access
                    it.
                  </li>
                  <li className="logo-note">
                    External users can also be invited using their email
                    addresses by adding them as regular users.
                  </li>
                </ul>
              </div>

              <Stack
                mt={3}
                direction={isMobile ? "column" : "row"}
                spacing={2.5}>
                <RHFTextField
                  name="ldap_url"
                  label="LDAP URL"
                  className="ldap-settings-bind-cred-text-field"
                />
                <RHFTextField
                  name="ldap_base_dn"
                  label="Search Base"
                  className="ldap-settings-bind-cred-text-field"
                />
              </Stack>
            </div>

            <div className="mt-32">
              <Typography className="setting-heading mb-4 ml-0">
                Bind Credentials
              </Typography>
              <Stack direction={isMobile ? "column" : "row"} spacing={2.5}>
                <RHFTextField
                  name="ldap_user"
                  label="Bind DN"
                  className="ldap-settings-bind-cred-text-field"
                />
                <RHFTextField
                  name="ldap_password"
                  label="Password"
                  type={"password"}
                  className="ldap-settings-bind-cred-text-field"
                />
              </Stack>
            </div>
            <Stack alignItems="end" className="mt-32">
              <CustomButton
                variant="contained"
                type="submit"
                className="theme-bg-color ldap-settings-bind-cred-update-button"
                disabled={isPending || !isAnyFieldFilled()}>
                Update
              </CustomButton>
            </Stack>
          </form>
        </div>
      </FormProvider>
    </>
  );
}

export default React.memo(LDAPSettingsBindCred);
